/**
 * client/src/components/layout/profile-context-chip.tsx
 *
 * Persistent "acting as <profile>" indicator in the app-shell header.
 *
 * Why it exists: which profile a user is acting as changes what every page
 * means — a deal, a connection, a message all belong to Personal, an org, or
 * the Platform Console — yet the only indicator was the sidebar switcher's
 * trigger, which renders a bare icon while the sidebar is collapsed and does
 * not exist at all on mobile (there it lives inside the ☰ More sheet, i.e.
 * behind a tap). This chip is on screen on every shell page, both breakpoints,
 * collapsed or not.
 *
 * Display-only by design: the switcher stays the single place a profile is
 * CHANGED, so this can never become a second, divergent switch affordance.
 * Colour is deliberately neutral — per CLAUDE.md Design Constraints emerald is
 * reserved for success/hardware-confirmed/AAL milestones, amber for user
 * errors/dormancy and red for Forensic Pause; "you are in the Platform
 * Console" is none of those, so the profile is distinguished by glyph + name
 * rather than by borrowing a status colour it would dilute.
 */

import { useLocation } from "wouter";
import { useOrgContext } from "@/lib/use-org-context";
import { profileFullName, resolveActiveProfile } from "@/lib/active-profile";
import { isPlatformRoute } from "@/lib/route-context";
import { Skeleton } from "@/components/ui/skeleton";
// The class string, not <SectionEyebrow> itself: this label needs aria-hidden
// (it is the sighted half of a sighted + sr-only pairing) and that component
// forwards no rest props.
import { SECTION_EYEBROW_TEXT_CLASS } from "@/components/premium";

export function ProfileContextChip(): JSX.Element | null {
  const { activeOrgId, activeOrgName, username, isLoading } = useOrgContext();
  const [location] = useLocation();

  // A placeholder, never a guess: while memberships are in flight activeOrgId
  // is not yet authoritative, and flashing "Personal" at an org user is exactly
  // the confusion this chip exists to remove. Reserving the footprint (rather
  // than rendering nothing) also keeps the header from reflowing when the real
  // chip arrives.
  if (isLoading) {
    return (
      <div className="flex min-w-0 items-center gap-2" data-testid="profile-context-chip-loading">
        <Skeleton className="h-5 w-28 rounded-full" />
      </div>
    );
  }

  const profile = resolveActiveProfile({
    onPlatform: isPlatformRoute(location),
    activeOrgId,
    activeOrgName,
    username,
  });

  return (
    <div
      // `cursor-default`: the pill looks tappable but is deliberately inert
      // (the switcher stays the only place a profile CHANGES), so the cursor
      // must not promise a click that does nothing.
      className="flex min-w-0 cursor-default items-center gap-2"
      title={`Acting as ${profileFullName(profile)}`}
      data-testid="profile-context-chip"
      data-profile-kind={profile.kind}
    >
      {/* aria-hidden: this is the SIGHTED copy of the label, and it is hidden
          below 640px to save horizontal room. The announced copy is the
          unconditional sr-only span below — exactly one of the two reaches a
          screen reader, so it never reads "Acting as Acting as Personal". */}
      <span
        aria-hidden="true"
        className={`hidden shrink-0 sm:inline ${SECTION_EYEBROW_TEXT_CLASS}`}
      >
        Acting as
      </span>
      <span className="inline-flex min-w-0 items-center gap-1.5 rounded-full border border-border-default bg-bg-elevated px-2.5 py-1">
        <profile.Icon size={13} className="shrink-0 text-text-tertiary" aria-hidden="true" />
        <span className="sr-only">Acting as </span>
        <span className="truncate text-xs font-medium text-text-primary">
          {profile.label}
          {profile.handle && (
            <span className="font-normal text-text-tertiary">{` · ${profile.handle}`}</span>
          )}
        </span>
      </span>
    </div>
  );
}
